import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { can as canRole, type BusinessContextDto, type MembershipSummary, type Permission, type Role, type TaxpayerDto } from '@portal/contracts';
import { get, setActiveBusinessId } from './api';
import { useSession } from './session';

const BIZ_KEY = 'portal.activeBusiness';
const taxKey = (b: string) => `portal.activeTaxpayer.${b}`;
const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* convenience only */
  }
};

interface BusinessValue {
  businessId: string;
  memberships: MembershipSummary[];
  setBusinessId: (id: string) => void;
  context: BusinessContextDto | undefined;
  isLoading: boolean;
  error: Error | null;
  role: Role | null;
  taxpayers: TaxpayerDto[];
  /** 'all' or a specific taxpayer profile id; used to scope lists and as the default for new records. */
  taxpayerScope: string;
  setTaxpayerScope: (id: string) => void;
  /** The taxpayer to preselect in forms: the chosen one, or the first. */
  defaultTaxpayer: TaxpayerDto | undefined;
  can: (p: Permission) => boolean;
}
const Ctx = createContext<BusinessValue | null>(null);

/** Query keys for business-scoped data always start with ['b', businessId] so switching business can never show stale data. */
export const bk = (businessId: string, ...rest: unknown[]) => ['b', businessId, ...rest] as const;

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const memberships = session?.memberships ?? [];
  const [stored, setStored] = useState<string | null>(() => read(BIZ_KEY));
  const businessId = memberships.find((m) => m.businessId === stored)?.businessId ?? memberships[0]?.businessId ?? '';

  // Set synchronously during render so child queries send the right X-Business-Id.
  setActiveBusinessId(businessId || null);

  const [taxpayerScope, setScopeState] = useState<string>(() => read(taxKey(businessId)) ?? 'all');
  useEffect(() => void setScopeState(read(taxKey(businessId)) ?? 'all'), [businessId]);

  const q = useQuery({
    queryKey: bk(businessId, 'context'),
    queryFn: () => get<BusinessContextDto>('/business'),
    enabled: !!businessId,
    staleTime: 60_000,
  });

  const value = useMemo<BusinessValue>(() => {
    const ctx = q.data;
    const taxpayers = ctx?.taxpayers ?? [];
    const scope = taxpayerScope !== 'all' && taxpayers.some((t) => t.id === taxpayerScope) ? taxpayerScope : 'all';
    return {
      businessId,
      memberships,
      setBusinessId: (id) => {
        write(BIZ_KEY, id);
        setStored(id);
      },
      context: ctx,
      isLoading: q.isLoading,
      error: q.error,
      role: ctx?.role ?? null,
      taxpayers,
      taxpayerScope: scope,
      setTaxpayerScope: (id) => {
        write(taxKey(businessId), id);
        setScopeState(id);
      },
      defaultTaxpayer: taxpayers.find((t) => t.id === scope) ?? taxpayers[0],
      can: (p) => canRole(ctx?.role, p),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, memberships.length, q.data, q.isLoading, q.error, taxpayerScope]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBusiness(): BusinessValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBusiness must be used inside <BusinessProvider>');
  return v;
}

/** `taxpayerProfileId` query param for list endpoints, honouring the global taxpayer selector. */
export function useTaxpayerParam(): string | undefined {
  const { taxpayerScope } = useBusiness();
  return taxpayerScope === 'all' ? undefined : taxpayerScope;
}
